import { build, isStampedBuild } from '@/lib/build';

/**
 * "Is a newer build live, and how do I get onto it?" — one module, no React.
 *
 * WHY THIS EXISTS. The console is an installed PWA on tills and phones. Those
 * never do a full page load, so the browser never re-checks the service
 * worker on its own and nothing in the app ever asked it to: a till opened on
 * Monday was still running Monday's build on Friday after a dozen deploys.
 * The only "update available" card lived on the POS Device page, and only if
 * the update happened to be detected while that page was open.
 *
 * WHAT IT DOES.
 * - Holds ONE update state that any screen can subscribe to
 *   (`onUpdateStateChange`; React reads it through src/pwa/use-update.ts).
 * - Checks for a new build on a timer, when the tab becomes visible, and when
 *   the device comes back online — by asking the browser to re-fetch sw.js
 *   (the service-worker layer registers that call with `setServiceWorkerCheck`)
 *   AND by comparing `version.json` on the server with the sha baked into this
 *   bundle. The second path works even where the worker failed to register.
 * - Applies the update on request (`requestReload`) through the `sg:sw-apply-
 *   update` seam that src/pwa/register-sw.ts owns, which activates a waiting
 *   worker when there is one and reloads the page.
 * - Reloads ONCE, automatically, when a route chunk fails to load because a
 *   deploy replaced it (`vite:preloadError`) — the white-screen-after-deploy
 *   case — and never loops.
 * - `repairApp` is the hard reload support asks for: drop the worker and
 *   every cache, then reload. Device preferences (localStorage) are kept.
 *
 * It never forces a reload on its own except the preload-error case; a cashier
 * mid-sale is never interrupted. A detected update remains pending until the
 * user chooses Reload now.
 */

export type UpdateSource = 'service-worker' | 'version-file' | 'chunk-load';

export interface UpdateState {
  /** A newer build is live and this tab is still running the old one. */
  available: boolean;
  /** How the update was first noticed; null while current. */
  source: UpdateSource | null;
  /** Sha of the build the server is serving, once version.json has answered. */
  liveSha: string | null;
  /** Last completed version.json check (epoch ms); null before the first. */
  checkedAt: number | null;
  /** A manual check is in flight (the Device page button). */
  checking: boolean;
  /** A reload was requested and is being carried out. */
  applying: boolean;
  /** The bar was dismissed; hidden until this time (epoch ms), 0 when not snoozed. */
  snoozedUntil: number;
}

export type UpdateCheckReason = 'startup' | 'interval' | 'visible' | 'online' | 'manual';

/** Window events shared with register-sw.ts (kept: the Device page tests speak them). */
export const UPDATE_AVAILABLE_EVENT = 'sg:sw-update-available';
export const APPLY_UPDATE_EVENT = 'sg:sw-apply-update';

const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const STARTUP_DELAY_MS = 8 * 1000;
const MIN_GAP_BETWEEN_AUTOMATIC_CHECKS_MS = 60 * 1000;
const DEFAULT_SNOOZE_MS = 30 * 60 * 1000;
/** Installing (register-sw.ts waits up to 4 min) + activating + reload; past this, something is wrong. */
const APPLY_TIMEOUT_MS = 5 * 60 * 1000;
const RELOADED_FOR_KEY = 'sg.update.reloaded-for';

const initialState: UpdateState = {
  available: false,
  source: null,
  liveSha: null,
  checkedAt: null,
  checking: false,
  applying: false,
  snoozedUntil: 0,
};

let state: UpdateState = initialState;
const listeners = new Set<(state: UpdateState) => void>();
let wired = false;
let watching = false;
let lastAutomaticCheckAt = 0;
let serviceWorkerCheck: (() => Promise<unknown>) | null = null;
let serviceWorkerActivateWaiting: (() => Promise<boolean>) | null = null;
let snoozeTimer: number | null = null;
let applyTimer: number | null = null;

export function readUpdateState(): UpdateState {
  return state;
}

function patch(changes: Partial<UpdateState>): void {
  state = { ...state, ...changes };
  for (const listener of listeners) listener(state);
}

/** Listen for the service-worker layer's "update waiting" announcement. Idempotent. */
function ensureWired(): void {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener(UPDATE_AVAILABLE_EVENT, onServiceWorkerWaiting);
}

/**
 * A waiting worker usually means a newer build. Not always: after a hard
 * refresh the page itself came from the network and IS the live build, and the
 * worker now waiting is its own — the old worker is merely still in charge.
 * Announcing "a new version is ready" there sends the user through a pointless
 * reload. So before announcing, compare version.json with this bundle: when
 * they match, activate the waiting worker quietly (clientsClaim then puts the
 * matching worker in charge of this page) and say nothing.
 *
 * Only when the service-worker layer is wired and the build is stamped; the
 * component tests dispatch this event with neither, and must never see a fetch.
 */
function onServiceWorkerWaiting(): void {
  if (!serviceWorkerActivateWaiting || !isStampedBuild()) {
    markUpdateAvailable('service-worker');
    return;
  }
  void reconcileWaitingWorker();
}

async function reconcileWaitingWorker(): Promise<void> {
  const live = await fetchLiveVersion();
  if (live && live.sha === build.sha && serviceWorkerActivateWaiting) {
    const activated = await serviceWorkerActivateWaiting().catch(() => false);
    if (activated) {
      patch({ checkedAt: Date.now(), liveSha: live.sha });
      return;
    }
  }
  markUpdateAvailable('service-worker', live?.sha ?? null);
}

export function onUpdateStateChange(listener: (state: UpdateState) => void): () => void {
  ensureWired();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function markUpdateAvailable(source: UpdateSource, liveSha: string | null = null): void {
  patch({
    available: true,
    source: state.source ?? source,
    liveSha: liveSha ?? state.liveSha,
  });
}

/** The service-worker layer hands over "ask the browser to re-fetch sw.js". */
export function setServiceWorkerCheck(check: (() => Promise<unknown>) | null): void {
  serviceWorkerCheck = check;
}

/** …and "activate the waiting worker without reloading" (resolves true once active). */
export function setServiceWorkerActivateWaiting(activate: (() => Promise<boolean>) | null): void {
  serviceWorkerActivateWaiting = activate;
}

interface LiveVersion {
  sha: string;
  builtAt: string;
}

async function fetchLiveVersion(): Promise<LiveVersion | null> {
  try {
    // `no-store` bypasses the HTTP cache; the service worker routes this path
    // NetworkOnly (vite.config.ts), so it is never answered from a cache.
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) return null;
    const { sha, builtAt } = body as Record<string, unknown>;
    if (typeof sha !== 'string' || sha === '') return null;
    return { sha, builtAt: typeof builtAt === 'string' ? builtAt : '' };
  } catch {
    // Offline, or the server is mid-deploy. The next trigger tries again.
    return null;
  }
}

/**
 * One check: nudge the browser to re-fetch the worker, then compare the live
 * version.json with this bundle. Automatic triggers are rate-limited so a
 * phone flipping between apps does not hammer the server; a manual check
 * always runs.
 */
export async function checkForUpdate(reason: UpdateCheckReason = 'manual'): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const now = Date.now();
  if (reason !== 'manual' && now - lastAutomaticCheckAt < MIN_GAP_BETWEEN_AUTOMATIC_CHECKS_MS) {
    return false;
  }
  lastAutomaticCheckAt = now;
  if (reason === 'manual') patch({ checking: true });

  try {
    if (serviceWorkerCheck) await serviceWorkerCheck().catch(() => undefined);

    const live = await fetchLiveVersion();
    if (!live) return false;

    const stale = isStampedBuild() && live.sha !== build.sha;
    patch({ checkedAt: Date.now(), liveSha: live.sha });
    if (stale) markUpdateAvailable('version-file', live.sha);
    return true;
  } finally {
    if (reason === 'manual') patch({ checking: false });
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') void checkForUpdate('visible');
}

/**
 * A lazily-loaded route chunk could not be fetched. On a static host that
 * only ever means one thing: a newer build replaced the hashed files this
 * index.html points at. Reloading lands on the new build. Guarded with the
 * sha so the same build can never reload itself in a loop — a second failure
 * on the same build is left to surface in the error boundary instead.
 */
function onPreloadError(event: Event): void {
  markUpdateAvailable('chunk-load');

  let reloadedFor: string | null = null;
  try {
    reloadedFor = sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch {
    // Private browsing: behave as if this is the first failure.
  }
  if (reloadedFor === build.sha) return;

  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, build.sha);
  } catch {
    // Without storage the guard is lost; one reload is still the right call.
  }
  event.preventDefault();
  void requestReload();
}

/**
 * Start watching. Called once from main.tsx in production builds only — dev
 * has no version.json and no service worker, and the component tests must
 * never see a fetch they did not mock.
 */
export function startUpdateWatch(): void {
  if (typeof window === 'undefined' || watching) return;
  watching = true;
  ensureWired();

  window.setInterval(() => void checkForUpdate('interval'), CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', () => void checkForUpdate('online'));
  window.addEventListener('vite:preloadError', onPreloadError);

  // A tab that just painted from a stale precache learns within seconds
  // rather than at the first 15-minute tick.
  window.setTimeout(() => void checkForUpdate('startup'), STARTUP_DELAY_MS);
}

/**
 * Get onto the new build. The service-worker layer owns the actual swap —
 * it has to activate a waiting worker before a reload can show anything new —
 * so this only announces the request on the shared seam.
 */
export function requestReload(): void {
  if (typeof window === 'undefined') return;
  if (state.applying) return;
  patch({ applying: true });
  // If the page is still here after this long, the swap failed somewhere
  // (worker stuck installing, browser refused). Hand the button back so the
  // user can try again or use Repair, instead of a spinner for ever.
  if (applyTimer !== null) window.clearTimeout(applyTimer);
  applyTimer = window.setTimeout(() => {
    applyTimer = null;
    patch({ applying: false });
  }, APPLY_TIMEOUT_MS);
  window.dispatchEvent(new CustomEvent(APPLY_UPDATE_EVENT));
}

/**
 * Hide the update bar for a while. The state stays "available", and the bar
 * comes back on its own when the snooze ends — the expiry is a timer here, so
 * no component has to read the clock during render to know whether it is
 * snoozed (React's purity rule), and the bar reappears at the right moment
 * rather than at the next check.
 */
export function snoozeUpdate(ms = DEFAULT_SNOOZE_MS): void {
  if (typeof window === 'undefined') return;
  if (snoozeTimer !== null) window.clearTimeout(snoozeTimer);
  patch({ snoozedUntil: Date.now() + ms });
  snoozeTimer = window.setTimeout(() => {
    snoozeTimer = null;
    patch({ snoozedUntil: 0 });
  }, ms);
}

/**
 * The hard reload. Unregisters every service worker and empties Cache Storage,
 * then reloads from the network. localStorage is untouched, so the login, the
 * selected terminal and the device preferences survive. Order notifications
 * ride on the worker registration, so a device that had them on must turn
 * them on again (Settings → Notifications re-attaches silently on its next
 * visit).
 */
export async function repairApp(): Promise<void> {
  if (typeof window === 'undefined') return;
  patch({ applying: true });
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister().catch(() => false)));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key).catch(() => false)));
    }
  } finally {
    window.location.reload();
  }
}
